import * as React from "react"
import { Integration } from "@modules/ui/components/Integration"
import { CmsIntegrationDropdown } from "./CmsIntegrationDropdown"
import { Button, Link, Text, Theme } from "gatsby-interface"
import {
  MdArrowForward,
  MdLaunch,
  MdDone,
  MdNotInterested,
} from "react-icons/md"
import { formatCmsConfiguration } from "../utils"
import { CmsProvider } from "@modules/graphql/types"

const cmsIntegrationRowCss = (theme: Theme) => ({
  borderTop: `1px solid ${theme.colors.grey[20]}`,
})

const cmsIntegrationRowStatusCss = (_theme: Theme) => ({
  cursor: `pointer`,
})

type CmsIntegrationRowProps = {
  cms: CmsProvider
  editable: boolean
  onEdit: () => void
  onDelete: () => void
}

export function CmsIntegrationRow({
  cms,
  editable,
  onEdit,
  onDelete,
}: CmsIntegrationRowProps) {
  const config = formatCmsConfiguration(cms)
  const isEditable = editable && cms.connected
  const hasConnectAction = editable && !cms.connected
  const showDetails = cms.connected && config.length > 0
  const cmsName = cms.formattedName || cms.name

  return (
    <Integration large={Boolean(cms.connected)} css={cmsIntegrationRowCss}>
      <Integration.Row>
        <Integration.Vendor image={cms.logoUrl} name={cmsName} />

        <Integration.Status
          connected={Boolean(cms.connected)}
          css={cmsIntegrationRowStatusCss}
        />

        <Integration.Actions>
          {hasConnectAction && (
            <span
              data-testid="connect-cms-button"
              data-cy={`connect-cms-integration-button-${cms.name}`}
            >
              <Button
                variant={`GHOST`}
                onClick={onEdit}
                rightIcon={<MdArrowForward />}
                aria-label={`Connect ${cmsName}`}
              >
                Connect
              </Button>
            </span>
          )}
          {isEditable && (
            <CmsIntegrationDropdown
              cmsName={cmsName}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          )}
        </Integration.Actions>
      </Integration.Row>

      {showDetails ? (
        <Integration.Details data-testid="cms-details">
          {config.map(item => (
            <Integration.Detail key={`data-${item.name}`}>
              <Integration.Label>{item.name}</Integration.Label>
              <Integration.Value>
                {item.url ? (
                  <Link
                    href={item.url}
                    target="_blank"
                    data-testid={`${item.name}-url`}
                  >
                    {item.value}
                    <MdLaunch />
                  </Link>
                ) : (
                  <IntegrationFeatureValue featureValue={item.value} />
                )}
              </Integration.Value>
            </Integration.Detail>
          ))}
        </Integration.Details>
      ) : null}
    </Integration>
  )
}

const checkedIconCss = (theme: Theme) => ({
  marginRight: theme.space[3],
  color: theme.colors.green[70],
  strokeWidth: 2,
})

const uncheckedIconCss = (theme: Theme) => ({
  marginRight: theme.space[3],
  color: theme.colors.grey[40],
  opacity: 0.5,
})

function IntegrationFeatureValue({
  featureValue,
}: {
  featureValue: string | boolean
}) {
  if (typeof featureValue !== `boolean`) {
    return <>{featureValue}</>
  }

  return (
    <Text
      size="S"
      css={{
        alignItems: `center`,
        display: `flex`,
        margin: 0,
      }}
    >
      {featureValue ? (
        <React.Fragment>
          <MdDone css={checkedIconCss} /> Installed
        </React.Fragment>
      ) : (
        <React.Fragment>
          <MdNotInterested css={uncheckedIconCss} /> Not installed
        </React.Fragment>
      )}
    </Text>
  )
}
